import React, { useState, useEffect, useRef } from 'react';
import { getProjets, createProjet, updateProjet, deleteProjet } from '../services/api';
import { FiPlus, FiEdit2, FiTrash2, FiSearch, FiX, FiUpload, FiMapPin, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import api from '../services/api';

const Projets = () => {
  const [projets, setProjets] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState('');
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [form, setForm] = useState({ libelle: '', localisation: '', dateprojet: '', superficie: '', nb_parcelles: '', description: '' });

  // GPX upload
  const [uploading, setUploading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [uploadProjetId, setUploadProjetId] = useState('');
  const uploadProjetIdRef = useRef('');
  const [showGpxPreview, setShowGpxPreview] = useState(false);
  const [gpxPreview, setGpxPreview] = useState<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadData = () => {
    api.get('/cartographie/projets').then(r => setProjets(r.data)).catch(() => {
      getProjets().then(r => setProjets(r.data)).catch(() => {});
    });
  };

  useEffect(() => { loadData(); }, []);

  const resetForm = () => setForm({ libelle: '', localisation: '', dateprojet: '', superficie: '', nb_parcelles: '', description: '' });

  const openAdd = () => { resetForm(); setEditingId(''); setShowModal(true); };
  const openEdit = (p: any) => {
    setForm({
      libelle: p.libelle || '',
      localisation: p.localisation || '',
      dateprojet: p.dateprojet ? p.dateprojet.substring(0, 10) : '',
      superficie: p.superficie || '',
      nb_parcelles: p.nb_parcelles || '',
      description: p.description || ''
    });
    setEditingId(p.id);
    setShowModal(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingId) {
        await updateProjet(editingId, form);
        setSuccess('Projet mis à jour');
      } else {
        await createProjet(form);
        setSuccess('Projet créé avec succès');
      }
      setShowModal(false);
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Supprimer ce projet ?')) return;
    try {
      await deleteProjet(id);
      setSuccess('Projet supprimé');
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur lors de la suppression');
      setTimeout(() => setError(''), 3000);
    }
  };

  // GPX upload
  const triggerGpxUpload = (projetId: string) => {
    setUploadProjetId(projetId);
    uploadProjetIdRef.current = projetId;
    fileInputRef.current?.click();
  };

  const handleGpxFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const pid = uploadProjetIdRef.current;
    if (!file || !pid) return;
    if (fileInputRef.current) fileInputRef.current.value = '';

    // Step 1: Upload for preview (no DB changes)
    setUploading(true);
    const formData = new FormData();
    formData.append('gpx', file);

    try {
      const r = await api.post(`/cartographie/preview-gpx/${pid}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setGpxPreview(r.data);
      setShowGpxPreview(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur lors de l\'analyse du fichier GPX');
      setTimeout(() => setError(''), 5000);
    } finally {
      setUploading(false);
    }
  };

  const confirmGpxImport = async (overwrite: boolean) => {
    if (!gpxPreview?.temp_filename) return;
    const pid = uploadProjetIdRef.current;
    setConfirming(true);

    try {
      const r = await api.post(`/cartographie/confirm-gpx/${pid}`, {
        temp_filename: gpxPreview.temp_filename,
        overwrite
      });
      setSuccess(`GPX importé: ${r.data.parcelles_creees} parcelles créées${r.data.anciennes_supprimees > 0 ? `, ${r.data.anciennes_supprimees} anciennes remplacées` : ''}`);
      setShowGpxPreview(false);
      setGpxPreview(null);
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur lors de l\'importation');
    } finally {
      setConfirming(false);
      setTimeout(() => { setSuccess(''); setError(''); }, 5000);
    }
  };

  const cancelGpxPreview = () => {
    if (gpxPreview?.temp_filename) {
      api.post('/cartographie/cancel-gpx', { temp_filename: gpxPreview.temp_filename }).catch(() => {});
    }
    setShowGpxPreview(false);
    setGpxPreview(null);
  };

  const filtered = projets.filter(p => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (p.libelle || '').toLowerCase().includes(q) ||
      (p.localisation || '').toLowerCase().includes(q) ||
      (p.reference || '').toLowerCase().includes(q);
  });

  return (
    <div>
      <h1 className="page-title">Gestion des projets</h1>

      {success && <div className="alert alert-success">{success}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      {/* Hidden file input for GPX */}
      <input type="file" ref={fileInputRef} accept=".gpx" onChange={handleGpxFileSelect} style={{ display: 'none' }} />

      <div className="card">
        <div className="card-header">
          <span>Projets ({filtered.length})</span>
          <button className="btn btn-success" onClick={openAdd}><FiPlus /> Nouveau projet</button>
        </div>
        <div className="card-body">
          <div className="toolbar">
            <div className="search-box">
              <FiSearch className="search-icon" />
              <input type="text" className="form-input" placeholder="Rechercher..." value={search} onChange={e => setSearch(e.target.value)} />
            </div>
          </div>

          <div className="table-responsive">
            <table className="data-table">
              <thead>
                <tr><th>Référence</th><th>Libellé</th><th>Localisation</th><th>Superficie</th><th>Parcelles</th><th>GPX</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {filtered.map(p => (
                  <tr key={p.id}>
                    <td>{p.reference || '-'}</td>
                    <td><strong>{p.libelle || '-'}</strong></td>
                    <td>{p.localisation || '-'}</td>
                    <td>{p.superficie || '-'}</td>
                    <td>{p.nb_parcelles || 0}</td>
                    <td>
                      {p.has_gpx ? (
                        <span className="badge badge-green"><FiCheckCircle style={{ verticalAlign: 'middle', marginRight: 3 }} /> Chargé</span>
                      ) : (
                        <span className="badge badge-gray">Non chargé</span>
                      )}
                    </td>
                    <td>
                      <div className="actions">
                        <button
                          className="btn-icon btn-primary"
                          title="Charger fichier GPX"
                          onClick={() => triggerGpxUpload(p.id)}
                          disabled={uploading}
                        >
                          <FiUpload />
                        </button>
                        {p.has_gpx && (
                          <a href={`/admin/cartographie`} className="btn-icon btn-info" title="Voir sur la carte">
                            <FiMapPin />
                          </a>
                        )}
                        <button className="btn-icon btn-warning" onClick={() => openEdit(p)}><FiEdit2 /></button>
                        <button className="btn-icon btn-danger" onClick={() => handleDelete(p.id)}><FiTrash2 /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={7} style={{ textAlign: 'center' }}>Aucun projet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal Projet */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingId ? 'Modifier le projet' : 'Nouveau projet'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}><FiX /></button>
            </div>
            <form onSubmit={handleSave}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group"><label>Libellé</label><input className="form-input" value={form.libelle} onChange={e => setForm({ ...form, libelle: e.target.value })} required /></div>
                  <div className="form-group"><label>Localisation</label><input className="form-input" value={form.localisation} onChange={e => setForm({ ...form, localisation: e.target.value })} /></div>
                </div>
                <div className="form-row">
                  <div className="form-group"><label>Date du projet</label><input type="date" className="form-input" value={form.dateprojet} onChange={e => setForm({ ...form, dateprojet: e.target.value })} /></div>
                  <div className="form-group"><label>Superficie</label><input type="number" className="form-input" value={form.superficie} onChange={e => setForm({ ...form, superficie: e.target.value })} /></div>
                </div>
                <div className="form-row">
                  <div className="form-group"><label>Nombre de parcelles</label><input type="number" className="form-input" value={form.nb_parcelles} onChange={e => setForm({ ...form, nb_parcelles: e.target.value })} /></div>
                  <div className="form-group"></div>
                </div>
                <div className="form-group"><label>Description</label><textarea className="form-input" rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>Annuler</button>
                <button type="submit" className="btn btn-primary">Enregistrer</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* GPX Preview / Recap Modal */}
      {showGpxPreview && gpxPreview && (
        <div className="modal-overlay" onClick={cancelGpxPreview}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className="modal-header">
              <h3><FiMapPin /> Récapitulatif du fichier GPX</h3>
              <button className="modal-close" onClick={cancelGpxPreview}><FiX /></button>
            </div>
            <div className="modal-body">
              {/* File info */}
              <div className="gpx-recap-section">
                <div className="gpx-recap-title">Fichier</div>
                <div className="gpx-recap-grid">
                  <div className="gpx-recap-item">
                    <span className="gpx-recap-label">Nom</span>
                    <span className="gpx-recap-value">{gpxPreview.file_original}</span>
                  </div>
                  <div className="gpx-recap-item">
                    <span className="gpx-recap-label">Taille</span>
                    <span className="gpx-recap-value">{(gpxPreview.file_size / 1024).toFixed(1)} Ko</span>
                  </div>
                </div>
              </div>

              {/* Parcelles info */}
              <div className="gpx-recap-section">
                <div className="gpx-recap-title">Parcelles</div>
                <div className="gpx-recap-grid">
                  <div className="gpx-recap-item highlight">
                    <span className="gpx-recap-label">Nombre total</span>
                    <span className="gpx-recap-value big">{gpxPreview.nb_parcelles}</span>
                  </div>
                  <div className="gpx-recap-item">
                    <span className="gpx-recap-label">Superficie totale</span>
                    <span className="gpx-recap-value">{gpxPreview.superficie_totale.toLocaleString('fr-FR')} m²</span>
                  </div>
                  <div className="gpx-recap-item">
                    <span className="gpx-recap-label">Superficie min</span>
                    <span className="gpx-recap-value">{gpxPreview.superficie_min.toLocaleString('fr-FR')} m²</span>
                  </div>
                  <div className="gpx-recap-item">
                    <span className="gpx-recap-label">Superficie max</span>
                    <span className="gpx-recap-value">{gpxPreview.superficie_max.toLocaleString('fr-FR')} m²</span>
                  </div>
                  <div className="gpx-recap-item">
                    <span className="gpx-recap-label">Superficie moyenne</span>
                    <span className="gpx-recap-value">{gpxPreview.superficie_moyenne.toLocaleString('fr-FR')} m²</span>
                  </div>
                </div>
              </div>

              {/* Coordinates */}
              <div className="gpx-recap-section">
                <div className="gpx-recap-title">Zone géographique</div>
                <div className="gpx-recap-grid">
                  <div className="gpx-recap-item">
                    <span className="gpx-recap-label">Latitude</span>
                    <span className="gpx-recap-value">{gpxPreview.bounds.south.toFixed(6)} — {gpxPreview.bounds.north.toFixed(6)}</span>
                  </div>
                  <div className="gpx-recap-item">
                    <span className="gpx-recap-label">Longitude</span>
                    <span className="gpx-recap-value">{gpxPreview.bounds.west.toFixed(6)} — {gpxPreview.bounds.east.toFixed(6)}</span>
                  </div>
                </div>
              </div>

              {/* Existing parcelles warning */}
              {gpxPreview.existing.has_data && (
                <div className="gpx-recap-section">
                  <div className="gpx-confirm-alert">
                    <FiAlertCircle />
                    <div>
                      <strong>Ce projet contient déjà {gpxPreview.existing.total} parcelle(s)</strong>
                      <br />
                      dont <strong>{gpxPreview.existing.attribuees}</strong> attribuée(s) à des membres
                      {gpxPreview.existing.non_attribuables > 0 && <> et <strong>{gpxPreview.existing.non_attribuables}</strong> non attribuable(s)</>}.
                      <br />
                      <span style={{ fontSize: 12 }}>En confirmant, les anciennes parcelles seront remplacées.</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
              <button className="btn btn-outline" onClick={cancelGpxPreview} disabled={confirming}>
                Annuler
              </button>
              <button
                className={`btn ${gpxPreview.existing.has_data ? 'btn-danger' : 'btn-success'}`}
                onClick={() => confirmGpxImport(gpxPreview.existing.has_data)}
                disabled={confirming}
              >
                <FiCheckCircle /> {confirming ? 'Importation...' : gpxPreview.existing.has_data ? 'Remplacer et importer' : 'Confirmer l\'importation'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Projets;
